import React, {useContext, useState} from 'react';
import { Collapse } from '@material-ui/core';
import SvgIcon from '../../Components/Icons/SvgIcon';
import ArrowDown from '../../Components/Icons/ArrowDown';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import resultsContext from '../../Foundations/Contexts/ResultsContext';
import {getSupplierName} from '../../Foundations/Utils/SupplierUtils';
import {IResultSummary} from '../../Foundations/Types/IResultsData';
import {IDepotSummary} from '../../Foundations/Types/IDepot';
import {getDepotAddress} from '../../Foundations/Utils/PDFUtils';

const PriceSummaryContainer: React.FC = () => {
    const { results, resultSummary, depotsAtArea } = useContext(resultsContext);
    const [expanded, setExpanded] = useState(false);

    const toggleExpand = () => {
        setExpanded(!expanded)
    }

    const renderSummaryItem = (title: string, price: string | number, supplier?: string) => (
        <div className="flex flex-col justify-between">
            <div className="text-xxs uppercase font-bold text-gray-700">{title}</div>
            <div className="text-2xl font-bold text-black-400">{price}</div>
            {supplier && <div className="text-sm text-grey-600">{supplier}</div>}
        </div>
    )

    const renderDepotRow = (depot: IDepotSummary, index: number) => (
        <span key={index} className={`grid grid-cols-3 grid-rows-1 items-center px-8 py-1.5 text-gray-700 ${index % 2 === 0 ? 'bg-blue-100' : 'bg-white'}`}>
            <div className="pr-5">
                <span className="block font-bold">{depot.supplier_name || getSupplierName(depot.supplier_code)} - {depot.depot_code}</span>
                <span>{depot.address || getDepotAddress(depotsAtArea, `${depot.depot_code}_${depot.supplier_code}`)}</span>
            </div>
            {
                depot.lowestPerDay !== null &&
                <div>
                        <span className="block font-bold">
                            ${depot.lowestPerDay.per_day_price.toFixed(2)} <span className="font-normal">{depot.lowestPerDay.duration}d</span>
                        </span>
                    <span>{depot.lowestPerDay.vehicle_data.name}</span>
                </div>
            }
            {
                depot.highestPerDay !== null &&
                <div>
                    <span className="block font-bold">
                        ${depot.highestPerDay.per_day_price.toFixed(2)} <span className="font-normal">{depot.highestPerDay.duration}d</span>
                    </span>
                    <span>{depot.highestPerDay.vehicle_data.name}</span>
                </div>
            }
        </span>
    )

    const renderDepotsPrices = (depotSummary: IDepotSummary[]) => (
        <span className="w-full">
            <div className="grid grid-cols-3 grid-rows-1 px-8 pb-3.5 items-center text-xxs uppercase bold text-gray-700">
                <span>DEPOT</span>
                <span>LOWEST PER DAY (AUD)</span>
                <span>HIGHEST PER DAY (AUD)</span>
            </div>
            <div>
                {depotSummary.map((depot, index) => renderDepotRow(depot, index))}
            </div>
        </span>
    )

    const renderContent = (summary: IResultSummary) => (
        <>
            <div className="w-full flex border-gray border-t">
                <div className="w-1/2 px-8 py-4 flex items-center justify-between">
                    {renderSummaryItem('DEPOTS IN RADIUS', summary.depotSummary.length)}
                    {renderSummaryItem('MODELS AVAILABLE', Object.keys(summary.models).length)}
                </div>
                <div className="w-1/2 px-8 py-4 flex items-center justify-between">
                    {renderSummaryItem('CARS AVAILABLE', results.length)}
                </div>
            </div>
            <div className="w-full py-4 flex border-gray border-t">
                {renderDepotsPrices(summary.depotSummary)}
            </div>
        </>
    )

    const renderSummaryHeaderItem = (
        title: string,
        price: string,
        priceInfo: string | null,
        subTitle: string,
        address: string
    ) => (
        <div className={`flex ${priceInfo !== null ? 'w-5/12' : 'flex-col'} justify-between`}>
            <div>
                <div className="text-xxs uppercase font-bold text-gray-700">{title}</div>
                <div className="flex items-center">
                <span className={`block ${priceInfo !== null ? 'w-half' : ''} text-2xl font-bold text-black-400`}>
                    {price}
                </span>
                    {
                        priceInfo !== null &&
                        <span className="text-gray-600 block text-xs p-2">
                        {priceInfo}
                    </span>
                    }
                </div>
                <div className="text-sm text-gray-600">
                    <span className="font-bold text-xs block my-1">{subTitle}</span>
                    <span className="text-xs block">{address}</span>
                </div>
            </div>
        </div>
    )

    if (resultSummary === null || resultSummary.lowestPerDay === null || resultSummary.highestPerDay === null) {
        return null;
    }

    return (
        <div className="rounded-2xl shadow bg-white my-4">
            <div className="flex justify-between" onClick={toggleExpand}>
                <div className="w-7/12 px-6 py-4 flex justify-between">
                    {renderSummaryHeaderItem(
                        'LOWEST PER DAY (AUD)',
                        `$${resultSummary.lowestPerDay.per_day_price.toFixed(2)}`,
                        `${resultSummary.lowestPerDay.supplier_name || getSupplierName(resultSummary.lowestPerDay.supplier_code)} ${resultSummary.lowestPerDay.duration}d`,
                        resultSummary.lowestPerDay.vehicle_data.name,
                        resultSummary.lowestPerDay.depot_address
                    )}
                    <div className="flex items-center px-4">
                        <ArrowForwardIcon className="text-gray-500"/>
                    </div>
                    {renderSummaryHeaderItem(
                        'HIGHEST PER DAY (AUD)',
                        `$${resultSummary.highestPerDay.per_day_price.toFixed(2)}`,
                        `${resultSummary.highestPerDay.supplier_name || getSupplierName(resultSummary.highestPerDay.supplier_code)} ${resultSummary.highestPerDay.duration}d`,
                        resultSummary.highestPerDay.vehicle_data.name,
                        resultSummary.highestPerDay.depot_address
                    )}
                </div>
                <div className="w-5/12 px-6 py-4 flex justify-between">
                    <div>
                        {
                            resultSummary.depotSummary[0].lowestPerDay !== null &&
                            resultSummary.depotSummary[0].highestPerDay !== null &&
                            renderSummaryHeaderItem(
                                'PER DAY CLOSEST DEPOT (AUD)',
                                `$${resultSummary.depotSummary[0].lowestPerDay.per_day_price.toFixed(2)} - $${resultSummary.depotSummary[0].highestPerDay.per_day_price.toFixed(2)}`,
                                null,
                                resultSummary.depotSummary[0].supplier_name || getSupplierName(resultSummary.depotSummary[0].supplier_code),
                                resultSummary.depotSummary[0].address || getDepotAddress(depotsAtArea, `${resultSummary.depotSummary[0].depot_code}_${resultSummary.depotSummary[0].supplier_code}`)
                            )
                        }
                    </div>
                    <div className="flex items-center text-blue justify-center cursor-pointer">
                        {expanded
                            ? <span className="flex items-center font-bold">Less <SvgIcon className="fill-blue transform -rotate-180"><ArrowDown/></SvgIcon></span>
                            : <span className="flex items-center font-bold">More <SvgIcon className="fill-blue"><ArrowDown/></SvgIcon></span>
                        }
                    </div>
                </div>
            </div>
            <Collapse in={expanded} timeout='auto' unmountOnExit>
                {renderContent(resultSummary)}
            </Collapse>
        </div>
    )
}

export default PriceSummaryContainer
